import React from "react";
import type { LucideIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { DeployMode, DeploymentStatus } from "../../../types/strategyDeploy";
import { MODE_LABEL, STATUS_LABEL } from "./liveStrategiesFormat";
import { MODE_ICON } from "./liveStrategiesUi";

export type LiveTone = "good" | "warn" | "bad" | "brand" | "neutral";

/** The app's filled gradient badges (Reports type badges, Strategy Lab status): white text. */
// Urbanist sits low in its line box: the padding lifts the text 1px; icons stay centred.
const BADGE_BASE_CLASS =
  "!rounded-[6px] !border-0 font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal !text-white shadow-none !pt-px !pb-[3px] leading-4 [&>svg]:translate-y-px";

const TONE_CLASS: Record<LiveTone, string> = {
  good: "bg-[image:var(--gradient-glacial-green)]",
  warn: "bg-[image:var(--gradient-glacial-orange)]",
  bad: "bg-[image:var(--gradient-glacial-red)]",
  brand: "bg-[image:var(--gradient-primary)]",
  neutral: "bg-[image:linear-gradient(135deg,#6b7280,#9ca3af)]",
};

export function LiveBadge({
  tone,
  icon: Icon,
  pulse,
  className,
  children,
  title,
  ...rest
}: {
  tone: LiveTone;
  icon?: LucideIcon;
  /** A small white pulse (something live: running, waiting for you). */
  pulse?: boolean;
  className?: string;
  children: React.ReactNode;
  title?: string;
} & Record<`data-${string}`, string | undefined>): JSX.Element {
  return (
    <Badge className={cn(BADGE_BASE_CLASS, TONE_CLASS[tone], "gap-1.5", className)} title={title} {...rest}>
      {pulse ? (
        <span className="relative flex size-1.5 shrink-0 translate-y-px">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-white opacity-70" />
          <span className="relative inline-flex size-1.5 rounded-full bg-white" />
        </span>
      ) : Icon ? (
        <Icon aria-hidden />
      ) : null}
      {children}
    </Badge>
  );
}

const STATUS_TONE: Record<DeploymentStatus, LiveTone> = {
  running: "good",
  paused: "warn",
  stopped: "neutral",
  killed: "bad",
  error: "bad",
};

export function DeploymentStatusBadge({ status }: { status: DeploymentStatus }): JSX.Element {
  return (
    <LiveBadge tone={STATUS_TONE[status] ?? "neutral"} pulse={status === "running"} data-live-status={status}>
      {STATUS_LABEL[status] ?? status}
    </LiveBadge>
  );
}

export function DeployModeBadge({ mode }: { mode: DeployMode }): JSX.Element {
  return (
    <LiveBadge tone="brand" icon={MODE_ICON[mode]} data-live-mode-badge={mode}>
      {MODE_LABEL[mode]}
    </LiveBadge>
  );
}

/** Square brand tile for an option's icon (wizard options, settings rows, empty state). */
export function LiveIconTile({ icon: Icon, className }: { icon: LucideIcon; className?: string }): JSX.Element {
  return (
    <span
      className={cn(
        "flex size-9 shrink-0 items-center justify-center rounded-[6px] bg-[image:var(--gradient-primary)] text-white",
        className
      )}
      aria-hidden
    >
      <Icon className="size-4 text-white" />
    </span>
  );
}
